import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { useMemo } from 'react';

const CHAR_STRIPE_BAR_STYLES = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  background: `repeating-linear-gradient(
      to right,
      transparent,
      transparent 3px,
      #FFF 3px,
      #FFF 8px
    )`,
  zIndex: 1,
};

export type StripeProgressChartBarItem = {
  label: string;
  color: string;
  percentage: number | undefined | null;
};

interface PreparedStripeProgressChartBarItem extends StripeProgressChartBarItem {
  percentage: number;
}

type StripePercentageChartBarProps = {
  items: StripeProgressChartBarItem[];
  barHeight?: string;
  showLegend?: boolean;
};

const DEFAULT_STRIPE_BAR_HEIGHT = '40px';

export const StripePercentageChartBar = ({
  items,
  barHeight = DEFAULT_STRIPE_BAR_HEIGHT,
  showLegend = true,
}: StripePercentageChartBarProps) => {
  const preparedItems = useMemo(() => {
    return items
      .filter(item => typeof item.percentage === 'number' && item.percentage > 0)
      .sort(
        (itemA, itemB) => (itemB.percentage as number) - (itemA.percentage as number)
      ) as PreparedStripeProgressChartBarItem[];
  }, [items]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: 1 }}>
      {showLegend && (
        <Grid
          container
          sx={{
            paddingBottom: 1,
          }}
        >
          {preparedItems.map(chartItem => (
            <Grid
              key={chartItem.color}
              sx={{
                marginRight: 3,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  marginRight: 1,
                  borderRadius: '50%',
                  backgroundColor: chartItem.color,
                  height: '0.8em',
                  width: '0.8em',
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  marginRight: 1,
                }}
              >
                {chartItem.label}
              </Typography>
              <Typography variant="body1">{chartItem.percentage}%</Typography>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid
        sx={{
          height: barHeight,
          bgColor: 'background.boxLight',
          position: 'relative',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
        data-test="stripe-chart-bar-container"
        container
      >
        {preparedItems.map(chartItem => (
          <Box
            sx={{
              width: `${chartItem.percentage}%`,
              height: '100%',
              backgroundColor: chartItem.color,
            }}
            key={chartItem.color}
          />
        ))}
        <Box sx={CHAR_STRIPE_BAR_STYLES} />
      </Grid>
    </Box>
  );
};
